import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Pause,
  Play,
  DollarSign,
  Loader2,
  CheckCircle,
  AlertCircle,
  Settings
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import ProductDrawer from '@/components/superadmin/ProductDrawer';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_TYPE_LABELS = {
  llc_formation: 'LLC Formation',
  c_corp_formation: 'C-Corp Formation',
  s_corp_formation: 'S-Corp Formation',
  ein_application: 'EIN Application',
  registered_agent: 'Registered Agent',
  mail_forwarding: 'Mail Forwarding',
  amendment: 'Amendments',
  // Canada
  ca_federal_incorporation: 'Federal Corporate Incorporation',
  ca_extra_provincial_registration: 'Extra-Provincial Registration',
  ca_federal_annual_return: 'Federal Annual Return',
  ca_provincial_annual_return: 'Provincial Annual Return',
  ca_t2_tax_return: 'Corporate Tax Return (T2)',
  ca_t2_account_activation: 'T2 Account Activation',
  ca_schedule_91: 'Schedule 91 (Treaty Disclosure)',
  ca_gst_hst_registration: 'GST/HST Registration',
  ca_gst_hst_return: 'GST/HST Sales Tax Return',
  // United Kingdom
  uk_ltd_registration: 'Ltd Company Registration',
  uk_confirmation_statement: 'Confirmation Statement (CS01)',
  uk_eccta_id_verification: 'ECCTA Biometric ID Verification',
  uk_ct600_tax_return: 'Corporation Tax (CT600)',
  uk_hmrc_ct_registration: 'HMRC Corporate Tax Registration',
  uk_vat_registration: 'VAT Registration & Filings',
  uk_vat_registration_nonresident: 'VAT Registration (Non-Establishment)',
  // Singapore
  sg_pte_ltd_incorporation: 'Pte. Ltd. Incorporation',
  sg_acra_annual_return: 'ACRA Annual Return',
  sg_corporate_secretary: 'Corporate Secretary Service',
  sg_nominee_director: 'Nominee Director Service',
  sg_corporate_tax_return: 'Corporate Tax (Form C-S / C)',
  sg_eci_filing: 'ECI Filing',
  sg_gst_registration: 'GST Registration Setup',
  sg_iras_corppass_config: 'IRAS CorpPass Configuration',
  // United Arab Emirates
  ae_free_zone_license_setup: 'Free Zone License Setup',
  ae_trade_license_renewal: 'Trade License Renewal',
  ae_aml_ubo_reporting: 'AML / UBO Reporting',
  ae_corporate_tax_return: 'Corporate Tax Return',
  ae_emaratax_ct_registration: 'EmaraTax Corporate Tax Registration',
  ae_esr_filing: 'ESR Filing',
  ae_vat_registration: 'VAT Registration',
};

const FREQUENCY_LABELS = {
  one_time: 'One Time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  duration_based: 'Duration',
};

const PRICING_LOGIC_LABELS = {
  FIXED_BY_COUNTRY: 'Fixed (Country)',
  VARIABLE_BY_STATE: 'Variable (State)',
  TAX_PREP_ONLY: 'Tax Prep Only',
  SERVICE_ONLY: 'Service Only',
  PREMIUM_SERVICE_ONLY: 'Premium Service',
};

const PRICING_LOGIC_STYLES = {
  FIXED_BY_COUNTRY: 'bg-blue-500/20 text-blue-400',
  VARIABLE_BY_STATE: 'bg-purple-500/20 text-purple-400',
  TAX_PREP_ONLY: 'bg-amber-500/20 text-amber-400',
  SERVICE_ONLY: 'bg-cyan-500/20 text-cyan-400',
  PREMIUM_SERVICE_ONLY: 'bg-pink-500/20 text-pink-400',
};

const SuperAdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const ITEMS_PER_PAGE = 50;
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const fetchProducts = async (page = 1, search = '', serviceType = 'all', country = 'all') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', ITEMS_PER_PAGE);
      
      if (search) {
        params.append('search', search);
      }
      if (serviceType && serviceType !== 'all') {
        params.append('service_type', serviceType);
      }
      if (country && country !== 'all') {
        params.append('country', country);
      }
      
      const response = await axios.get(`${API}/admin/products?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProducts(response.data.products || []);
      setTotalProducts(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      setCurrentPage(response.data.page || 1);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1, searchTerm, serviceTypeFilter, countryFilter);
  }, [token]);
  
  // Debounced search - trigger after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchTerm) {
        setSearchTerm(searchInput);
        setCurrentPage(1);
        fetchProducts(1, searchInput, serviceTypeFilter, countryFilter);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);
  
  // Handle service type filter change
  const handleServiceTypeChange = (value) => {
    setServiceTypeFilter(value);
    setCurrentPage(1);
    fetchProducts(1, searchTerm, value, countryFilter);
  };

  // Handle country filter change
  const handleCountryChange = (value) => {
    setCountryFilter(value);
    setCurrentPage(1);
    fetchProducts(1, searchTerm, serviceTypeFilter, value);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchProducts(newPage, searchTerm, serviceTypeFilter, countryFilter);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Open drawer for add
  const handleAddProduct = () => {
    setEditingProduct(null);
    setDrawerOpen(true);
  };

  // Open drawer for edit
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setDrawerOpen(true);
  };

  // Pause product
  const handlePauseProduct = async (product) => {
    if (!window.confirm('Pausing this product will stop new orders. Existing orders are not affected. Continue?')) {
      return;
    }
    
    setActionLoading(product.id);
    setError('');
    try {
      await axios.put(`${API}/admin/products/${product.id}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Product paused');
      fetchProducts(currentPage, searchTerm, serviceTypeFilter, countryFilter);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to pause product'));
    } finally {
      setActionLoading(null);
    }
  };

  // Unpause product
  const handleUnpauseProduct = async (product) => {
    setActionLoading(product.id);
    setError('');
    try {
      await axios.put(`${API}/admin/products/${product.id}/unpause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Product unpaused');
      fetchProducts(currentPage, searchTerm, serviceTypeFilter, countryFilter);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to unpause product'));
    } finally {
      setActionLoading(null);
    }
  };

  // Delete product
  const handleDeleteProduct = async (product) => {
    if (!window.confirm('This will deactivate the product permanently. Existing orders are not affected. Continue?')) {
      return;
    }
    
    setActionLoading(product.id);
    setError('');
    try {
      await axios.delete(`${API}/admin/products/${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Product deleted');
      fetchProducts(currentPage, searchTerm, serviceTypeFilter, countryFilter);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete product');
    } finally {
      setActionLoading(null);
    }
  };

  // Client-side status filtering (applied after server-side fetch)
  const filteredProducts = products.filter(product => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return product.is_active && !product.is_paused;
    if (statusFilter === 'paused') return product.is_paused;
    return true;
  });

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Success Toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-gray-400 text-sm mt-1">{totalProducts.toLocaleString()} total products</p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          <Link
            to="/superadmin/products/state-fees"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-300 border border-gray-700 hover:bg-gray-800 transition-colors"
            data-testid="manage-state-fees-btn"
          >
            <Settings className="w-4 h-4" />
            Manage State Fees
          </Link>
          <Link
            to="/superadmin/products/state-addresses"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-300 border border-gray-700 hover:bg-gray-800 transition-colors"
            data-testid="manage-state-addresses-btn"
          >
            <Settings className="w-4 h-4" />
            Manage State Addresses
          </Link>
          <Link
            to="/superadmin/products/state-filings"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-300 border border-gray-700 hover:bg-gray-800 transition-colors"
            data-testid="manage-state-filings-btn"
          >
            <Settings className="w-4 h-4" />
            Manage State Filings
          </Link>
          <button
            onClick={handleAddProduct}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white"
            style={{ backgroundColor: '#00E87A' }}
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search by product name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#00E87A]"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={serviceTypeFilter}
            onChange={(e) => handleServiceTypeChange(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            data-testid="service-type-filter"
          >
            <option value="all">All Services</option>
            <optgroup label="United States">
              <option value="llc_formation">LLC Formation</option>
              <option value="c_corp_formation">C-Corp Formation</option>
              <option value="s_corp_formation">S-Corp Formation</option>
              <option value="ein_application">EIN Application</option>
              <option value="registered_agent">Registered Agent</option>
              <option value="mail_forwarding">Mail Forwarding</option>
              <option value="amendment">Amendments</option>
            </optgroup>
            <optgroup label="Canada">
              <option value="ca_federal_incorporation">Federal Corporate Incorporation</option>
              <option value="ca_extra_provincial_registration">Extra-Provincial Registration</option>
              <option value="ca_federal_annual_return">Federal Annual Return</option>
              <option value="ca_provincial_annual_return">Provincial Annual Return</option>
              <option value="ca_t2_tax_return">Corporate Tax Return (T2)</option>
              <option value="ca_t2_account_activation">T2 Account Activation</option>
              <option value="ca_schedule_91">Schedule 91 (Treaty Disclosure)</option>
              <option value="ca_gst_hst_registration">GST/HST Registration</option>
              <option value="ca_gst_hst_return">GST/HST Sales Tax Return</option>
            </optgroup>
            <optgroup label="United Kingdom">
              <option value="uk_ltd_registration">Ltd Company Registration</option>
              <option value="uk_confirmation_statement">Confirmation Statement (CS01)</option>
              <option value="uk_eccta_id_verification">ECCTA Biometric ID Verification</option>
              <option value="uk_ct600_tax_return">Corporation Tax (CT600)</option>
              <option value="uk_hmrc_ct_registration">HMRC Corporate Tax Registration</option>
              <option value="uk_vat_registration">VAT Registration & Filings</option>
              <option value="uk_vat_registration_nonresident">VAT Registration (Non-Establishment)</option>
            </optgroup>
            <optgroup label="Singapore">
              <option value="sg_pte_ltd_incorporation">Pte. Ltd. Incorporation</option>
              <option value="sg_acra_annual_return">ACRA Annual Return</option>
              <option value="sg_corporate_secretary">Corporate Secretary Service</option>
              <option value="sg_nominee_director">Nominee Director Service</option>
              <option value="sg_corporate_tax_return">Corporate Tax (Form C-S / C)</option>
              <option value="sg_eci_filing">ECI Filing</option>
              <option value="sg_gst_registration">GST Registration Setup</option>
              <option value="sg_iras_corppass_config">IRAS CorpPass Configuration</option>
            </optgroup>
            <optgroup label="United Arab Emirates">
              <option value="ae_free_zone_license_setup">Free Zone License Setup</option>
              <option value="ae_trade_license_renewal">Trade License Renewal</option>
              <option value="ae_aml_ubo_reporting">AML / UBO Reporting</option>
              <option value="ae_corporate_tax_return">Corporate Tax Return</option>
              <option value="ae_emaratax_ct_registration">EmaraTax Corporate Tax Registration</option>
              <option value="ae_esr_filing">ESR Filing</option>
              <option value="ae_vat_registration">VAT Registration</option>
            </optgroup>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={countryFilter}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            data-testid="country-filter"
          >
            <option value="all">All Countries</option>
            <option value="US">United States</option>
            <option value="UK">United Kingdom</option>
            <option value="UAE">United Arab Emirates</option>
            <option value="SG">Singapore</option>
            <option value="CA">Canada</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-500" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-[#161B22] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#00E87A]"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
          </select>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-[#161B22] border border-gray-800 rounded-lg overflow-hidden">
        {filteredProducts.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Product Name</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Service Type</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Category</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Pricing Logic</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Our Fee</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden lg:table-cell">State Fee</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Frequency</th>
                  <th className="text-left text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Status</th>
                  <th className="text-right text-gray-500 text-xs font-medium uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredProducts.map((product) => {
                  const isLoading = actionLoading === product.id;
                  const frequency = product.frequency_settings?.frequency || 'one_time';
                  
                  return (
                    <tr key={product.id} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#00E87A]/10 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#00E87A]" />
                          </div>
                          <div>
                            <p className="text-white font-medium text-sm">{product.product_name}</p>
                            <p className="text-gray-500 text-xs md:hidden">
                              {SERVICE_TYPE_LABELS[product.service_type] || product.service_type}
                            </p>
                            {product.filing_notes && (
                              <p className="text-gray-500 text-xs mt-1 max-w-md truncate" title={product.filing_notes}>
                                {product.filing_notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="text-gray-300 text-sm">
                          {SERVICE_TYPE_LABELS[product.service_type] || product.service_type}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <span className="text-gray-300 text-sm">
                          {product.category || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {product.pricing_logic ? (
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${PRICING_LOGIC_STYLES[product.pricing_logic] || 'bg-gray-600/20 text-gray-400'}`}>
                            {PRICING_LOGIC_LABELS[product.pricing_logic] || product.pricing_logic}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4 text-[#00E87A]" />
                          <span className="text-white font-medium text-sm">
                            {formatAmount(product.platform_fees)}
                          </span>
                        </div>
                        {product.resolved_final_amount != null && (
                          <p className="text-gray-500 text-xs mt-0.5">
                            + {formatAmount(product.resolved_govt_fee)} govt = <span className="text-[#00E87A]">{formatAmount(product.resolved_final_amount)}</span>
                          </p>
                        )}
                        {product.govt_fee_varies && (
                          <p className="text-gray-500 text-xs mt-0.5">+ govt fee varies by region</p>
                        )}
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {product.resolved_govt_fee != null ? (
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                            {formatAmount(product.resolved_govt_fee)}
                          </span>
                        ) : product.govt_fee_varies ? (
                          <span
                            className="inline-flex px-2 py-1 rounded text-xs font-medium bg-purple-500/20 text-purple-400"
                            title={product.govt_fee_min != null ? `Range ${formatAmount(product.govt_fee_min)} – ${formatAmount(product.govt_fee_max)}` : 'Set per region in State Fees'}
                          >
                            Varies
                          </span>
                        ) : (
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            product.govt_fees > 0 || product.has_state_fee
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-gray-600/20 text-gray-400'
                          }`}>
                            {product.govt_fees > 0 || product.has_state_fee ? 'Yes' : 'No'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className="text-gray-300 text-sm">
                          {FREQUENCY_LABELS[frequency] || frequency}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {product.is_paused ? (
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                            Paused
                          </span>
                        ) : product.is_active ? (
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-[#00E87A]/20 text-[#00E87A]">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-gray-600/20 text-gray-400">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-1">
                          {/* Edit */}
                          <button
                            onClick={() => handleEditProduct(product)}
                            disabled={isLoading}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                            title="Edit"
                            data-testid={`edit-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          
                          {/* Pause / Unpause */}
                          {product.is_paused ? (
                            <button
                              onClick={() => handleUnpauseProduct(product)}
                              disabled={isLoading}
                              className="p-2 text-gray-400 hover:text-[#00E87A] hover:bg-[#00E87A]/10 rounded-lg transition-colors disabled:opacity-50"
                              title="Unpause"
                              data-testid={`unpause-${product.id}`}
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </button>
                          ) : (
                            <button
                              onClick={() => handlePauseProduct(product)}
                              disabled={isLoading}
                              className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors disabled:opacity-50"
                              title="Pause"
                              data-testid={`pause-${product.id}`}
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />}
                            </button>
                          )}
                          
                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteProduct(product)}
                            disabled={isLoading}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                            data-testid={`delete-${product.id}`}
                          >
                            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-sm">
            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalProducts)} of {totalProducts.toLocaleString()} products
          </p>
          
          <div className="flex items-center gap-2">
            {/* Previous */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              className="px-4 py-2 bg-[#161B22] border border-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
              data-testid="prev-page-btn"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {(() => {
                const pages = [];
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = Math.min(totalPages, start + maxVisible - 1);
                
                if (end - start + 1 < maxVisible) {
                  start = Math.max(1, end - maxVisible + 1);
                }
                
                if (start > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => handlePageChange(1)}
                      className="w-10 h-10 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
                    >
                      1
                    </button>
                  );
                  if (start > 2) {
                    pages.push(<span key="dots1" className="text-gray-500 px-1">...</span>);
                  }
                }
                
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => handlePageChange(i)}
                      disabled={loading}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        i === currentPage
                          ? 'bg-[#00E87A] text-black font-medium'
                          : 'text-gray-400 hover:bg-gray-800'
                      }`}
                      data-testid={`page-${i}-btn`}
                    >
                      {i}
                    </button>
                  );
                }
                
                if (end < totalPages) {
                  if (end < totalPages - 1) {
                    pages.push(<span key="dots2" className="text-gray-500 px-1">...</span>);
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => handlePageChange(totalPages)}
                      className="w-10 h-10 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
                    >
                      {totalPages}
                    </button>
                  );
                }
                
                return pages;
              })()}
            </div>
            
            {/* Next */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || loading}
              className="px-4 py-2 bg-[#161B22] border border-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
              data-testid="next-page-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Product Drawer */}
      <ProductDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        product={editingProduct}
        token={token}
        onSuccess={(message) => {
          showSuccess(message);
          fetchProducts(currentPage, searchTerm, serviceTypeFilter, countryFilter);
        }}
      />
    </SuperAdminLayout>
  );
};

export default SuperAdminProducts;
